import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Warning, SignOut, Eye, FunnelSimple, ChatCircleText, MapPin, MagnifyingGlass } from '@phosphor-icons/react';

const statusColors = {
  'Yeni': 'bg-[hsl(var(--primary))] text-white',
  'İnceleniyor': 'bg-[hsl(var(--accent))] text-black',
  'İşleme Alındı': 'bg-blue-600 text-white',
  'Kapatıldı': 'bg-[hsl(var(--success))] text-white',
};

export default function OfficerDashboard() {
  const { user, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef(null);

  const handleSearchChange = (val) => {
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(val.length >= 3 ? val : '');
      setPage(1);
    }, 300);
  };

  const fetchReports = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (cityFilter !== 'all') params.city = cityFilter;
    if (searchQuery) params.search = searchQuery;
    authAxios().get('/reports', { params })
      .then(res => { setReports(res.data.reports); setTotal(res.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authAxios, statusFilter, cityFilter, searchQuery, page]);

  useEffect(() => {
    authAxios().get('/reports/cities').then(res => setCities(res.data)).catch(() => {});
  }, [authAxios]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="bg-[#1C1C1E] border-b border-[#2C2C2E] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Warning weight="fill" className="w-6 h-6 text-[hsl(var(--primary))]" />
          <span className="font-display text-xl text-white">MEMUR PANELI</span>
          {!loading && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-bold">{total}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[hsl(var(--muted-foreground))] hidden sm:inline">{user?.full_name}</span>
          <Button
            data-testid="logout-button"
            variant="ghost"
            onClick={() => { logout(); navigate('/login'); }}
            className="text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-[#2C2C2E] h-10"
          >
            <SignOut weight="bold" className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="p-4 max-w-4xl mx-auto">
        {/* Search */}
        <div className="relative mb-3">
          <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <Input
            data-testid="search-input"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="İhbar no, kurum, şehir, durum ara... (min 3 karakter)"
            className="h-10 pl-10 bg-[#1C1C1E] border-[#2C2C2E] text-white placeholder:text-[#666] text-sm"
          />
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-3 mb-4">
          <FunnelSimple weight="bold" className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger data-testid="status-filter" className="w-48 h-10 bg-[#1C1C1E] border-[#2C2C2E] text-white">
              <SelectValue placeholder="Durum filtrele" />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
              <SelectItem value="all" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Tümü</SelectItem>
              <SelectItem value="Yeni" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Yeni</SelectItem>
              <SelectItem value="İnceleniyor" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">İnceleniyor</SelectItem>
              <SelectItem value="İşleme Alındı" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">İşleme Alındı</SelectItem>
              <SelectItem value="Kapatıldı" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Kapatıldı</SelectItem>
            </SelectContent>
          </Select>
          {cities.length > 0 && (
            <Select value={cityFilter} onValueChange={(v) => { setCityFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="city-filter" className="w-44 h-10 bg-[#1C1C1E] border-[#2C2C2E] text-white">
                <SelectValue placeholder="Şehir" />
              </SelectTrigger>
              <SelectContent className="bg-[#1C1C1E] border-[#2C2C2E]">
                <SelectItem value="all" className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">Tüm Şehirler</SelectItem>
                {cities.map(c => (
                  <SelectItem key={c} value={c} className="text-white hover:bg-[#2C2C2E] focus:bg-[#2C2C2E] focus:text-white">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">Henüz ihbar yok</div>
        ) : (
          <div className="space-y-3">
            {reports.map(report => (
              <Card
                key={report.id}
                data-testid={`report-card-${report.id}`}
                className="bg-[#1C1C1E] border-[#2C2C2E] hover:border-[#3C3C3E] transition-colors cursor-pointer"
                onClick={() => navigate(`/officer/report/${report.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-heading text-white">{report.report_no}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{formatDate(report.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${statusColors[report.status] || 'bg-[#2C2C2E] text-white'} text-xs`}>
                        {report.status}
                      </Badge>
                      <Eye weight="bold" className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
                    </div>
                  </div>
                  {report.notes_from_reporter && (
                    <p className="text-sm text-[hsl(var(--muted-foreground))] truncate">{report.notes_from_reporter}</p>
                  )}
                  {report.latitude && (
                    <div className="mt-1.5 mb-1">
                      <button
                        data-testid={`location-link-${report.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const label = report.report_no.slice(-6);
                          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                          const url = isIOS
                            ? `maps://maps.apple.com/?q=${encodeURIComponent(label)}&ll=${report.latitude},${report.longitude}&z=17`
                            : `https://www.google.com/maps?q=${report.latitude},${report.longitude}+(${label})`;
                          window.open(url, '_blank');
                        }}
                        className="flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] hover:text-white transition-colors"
                      >
                        <MapPin weight="fill" className="w-3.5 h-3.5" />
                        <span className="underline underline-offset-2">{report.city || 'Konumu Gör'}</span>
                      </button>
                    </div>
                  )}
                  {report.last_note && (
                    <div className="mt-2 flex items-start gap-2 bg-[#0A0A0A] rounded-md px-3 py-2">
                      <ChatCircleText weight="fill" className="w-4 h-4 text-[hsl(var(--accent))] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-xs text-[hsl(var(--accent))] font-medium">{report.last_note_status}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{report.last_note}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              variant="outline"
              className="border-[#2C2C2E] text-white hover:bg-[#2C2C2E] hover:text-white h-10"
            >
              Önceki
            </Button>
            <Button
              disabled={page * 20 >= total}
              onClick={() => setPage(p => p + 1)}
              variant="outline"
              className="border-[#2C2C2E] text-white hover:bg-[#2C2C2E] hover:text-white h-10"
            >
              Sonraki
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
